import {
  type FunnelCounts,
  type Money,
  type Percentiles,
  TURN_BUDGETS,
  USAGE_PERIODS,
  type UsagePeriod,
  type UsageReport,
  withinBudget,
} from "@procuremate/shared";
import { clsx } from "clsx";
import { CircleAlert, CircleCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Card } from "./admin-layout";
import { useUsage } from "./api";

const KNOWN_TASKS = ["conversation", "review", "summary", "doc_extraction", "embedding", "classification"];

function usePeriod(): UsagePeriod {
  const [params] = useSearchParams();
  const days = Number(params.get("days"));
  return (USAGE_PERIODS as readonly number[]).includes(days) ? (days as UsagePeriod) : 30;
}

/** Number, money, duration and share formatting in the UI language. */
function useFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const number = new Intl.NumberFormat(lang);
  const money = (list: Money[]) =>
    list.length === 0
      ? new Intl.NumberFormat(lang, { style: "currency", currency: "USD" }).format(0)
      : list
          .map((m) => {
            const amount = m.micros / 1_000_000;
            return new Intl.NumberFormat(lang, {
              style: "currency",
              currency: m.currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: amount !== 0 && Math.abs(amount) < 1 ? 4 : 2,
            }).format(amount);
          })
          .join(" + ");
  const seconds = (ms: number | null) =>
    ms === null
      ? "—"
      : t("admin.usage.seconds", {
          value: new Intl.NumberFormat(lang, { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(ms / 1000),
        });
  const percent = (share: number | null) =>
    share === null ? "—" : new Intl.NumberFormat(lang, { style: "percent", maximumFractionDigits: 0 }).format(share);
  const taskName = (task: string) => (KNOWN_TASKS.includes(task) ? t(`admin.usage.tasks.${task}`) : task);
  return { number: (v: number) => number.format(v), money, seconds, percent, taskName };
}

/**
 * Admin → Usage (WP-3.7, ADR-0025): model cost, turn latency against its budget, failures and the
 * request funnel. Aggregates only — never a person or a request.
 */
export function UsagePage() {
  const { t } = useTranslation();
  const days = usePeriod();
  const usage = useUsage(days);
  const report = usage.data;
  const empty = report ? report.totals.calls === 0 && report.funnel.overall.started === 0 : false;

  return (
    <div className="flex flex-col gap-6">
      <Card title={t("admin.usage.title")} headingLevel={1} actions={<PeriodPicker current={days} />}>
        <p className="max-w-prose text-sm text-fg-muted">{t("admin.usage.intro")}</p>
        {usage.isPending ? (
          <p className="mt-4 text-fg-muted">{t("app.loading")}</p>
        ) : usage.isError ? (
          <p role="alert" className="mt-4 text-danger">
            {t("admin.usage.loadError")}
          </p>
        ) : empty ? (
          <p className="mt-4 text-fg">{t("admin.usage.empty", { count: days })}</p>
        ) : null}
        {report && !empty ? <Summary report={report} /> : null}
      </Card>
      {report && !empty ? (
        <>
          <DailyCost report={report} />
          <Breakdown report={report} />
          <Funnel report={report} />
        </>
      ) : null}
    </div>
  );
}

function PeriodPicker({ current }: { current: UsagePeriod }) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("admin.usage.period")} className="flex rounded-md border border-border p-0.5">
      {USAGE_PERIODS.map((d) => (
        <Link
          key={d}
          to={`?days=${d}`}
          aria-current={d === current ? "page" : undefined}
          className={clsx(
            "rounded-sm px-3 py-1.5 text-sm font-semibold",
            d === current ? "bg-primary text-on-primary" : "text-primary hover:bg-bg",
          )}
        >
          {t("admin.usage.days", { count: d })}
        </Link>
      ))}
    </nav>
  );
}

function Summary({ report }: { report: UsageReport }) {
  const { t } = useTranslation();
  const f = useFormat();
  const { totals, turns } = report;
  return (
    <>
      <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Figure label={t("admin.usage.cost")} value={f.money(totals.costs)}>
          {t("admin.usage.calls", { count: totals.calls, formatted: f.number(totals.calls) })}
        </Figure>
        <Figure label={t("admin.usage.turns")} value={f.number(turns.count)}>
          {t("admin.usage.costPerTurn", { cost: f.money(turns.costPerTurn) })}
        </Figure>
        <Figure label={t("admin.usage.cacheShare")} value={f.percent(turns.cachedShare)}>
          {t("admin.usage.cacheHint")}
        </Figure>
        <LatencyFigure
          label={t("admin.usage.firstText")}
          measured={turns.firstTextMs}
          budget={TURN_BUDGETS.firstText}
        />
        <LatencyFigure label={t("admin.usage.fullTurn")} measured={turns.fullTurnMs} budget={TURN_BUDGETS.fullTurn} />
        <Figure
          label={t("admin.usage.failures")}
          value={f.percent(totals.calls > 0 ? totals.failed / totals.calls : null)}
        >
          {t("admin.usage.failuresHint", { failed: f.number(totals.failed), fallbacks: f.number(totals.fallbacks) })}
        </Figure>
      </dl>
      {report.unlinked.calls > 0 ? (
        <p className="mt-4 text-sm text-fg-muted">
          {t("admin.usage.unlinked", {
            count: report.unlinked.calls,
            formatted: f.number(report.unlinked.calls),
            cost: f.money(report.unlinked.costs),
          })}
        </p>
      ) : null}
    </>
  );
}

function Figure({ label, value, children }: { label: string; value: ReactNode; children?: ReactNode }) {
  return (
    <div className="rounded-md border border-border p-4">
      <dt className="text-sm text-fg-muted">{label}</dt>
      <dd className="mt-1 font-heading text-2xl font-bold text-primary tabular">{value}</dd>
      {children ? <dd className="mt-1 text-sm text-fg-muted">{children}</dd> : null}
    </div>
  );
}

/** p50 / p95 against the budget: met or missed in words and an icon, never colour alone. */
function LatencyFigure({
  label,
  measured,
  budget,
}: {
  label: string;
  measured: Percentiles;
  budget: { p50: number; p95: number };
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const met = [withinBudget(measured.p50, budget.p50), withinBudget(measured.p95, budget.p95)];
  const verdict = met.includes(null) ? null : met.every(Boolean);
  return (
    <Figure label={label} value={`${f.seconds(measured.p50)} / ${f.seconds(measured.p95)}`}>
      <span className="block">
        {t("admin.usage.budget", { p50: f.seconds(budget.p50), p95: f.seconds(budget.p95) })}
      </span>
      {verdict === null ? null : (
        <span
          className={clsx(
            "mt-1 inline-flex items-center gap-1 font-semibold",
            verdict ? "text-success" : "text-danger",
          )}
        >
          {verdict ? (
            <CircleCheck aria-hidden="true" className="h-4 w-4" />
          ) : (
            <CircleAlert aria-hidden="true" className="h-4 w-4" />
          )}
          {verdict ? t("admin.usage.budgetMet") : t("admin.usage.budgetMissed")}
        </span>
      )}
    </Figure>
  );
}

/** Daily cost in the main currency as bars (decoration) and the same figures as a table. */
function DailyCost({ report }: { report: UsageReport }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const currency = report.totals.costs[0]?.currency ?? "USD";
  const amount = (costs: Money[]) => costs.find((c) => c.currency === currency)?.micros ?? 0;
  const max = Math.max(1, ...report.daily.map((d) => amount(d.costs)));
  const day = new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", timeZone: "UTC" });
  const label = (date: string) => day.format(new Date(`${date}T00:00:00Z`));
  return (
    <Card title={t("admin.usage.dailyTitle")}>
      <p className="mb-3 text-sm text-fg-muted">{t("admin.usage.dailyHint", { currency })}</p>
      <div aria-hidden="true" className="flex h-32 items-end gap-px border-b border-border">
        {report.daily.map((d) => (
          <div
            key={d.date}
            title={`${label(d.date)}: ${f.money(d.costs)}`}
            className="min-w-0 flex-1 rounded-t-sm bg-primary"
            style={{ height: `${Math.max(amount(d.costs) > 0 ? 2 : 0, (amount(d.costs) / max) * 100)}%` }}
          />
        ))}
      </div>
      <div aria-hidden="true" className="mt-1 flex justify-between text-xs text-fg-muted">
        <span>{label(report.period.from)}</span>
        <span>{label(report.period.to)}</span>
      </div>
      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-semibold text-link">{t("admin.usage.dailyTable")}</summary>
        <TableRegion label={t("admin.usage.dailyTitle")}>
          <table className="mt-2 w-full text-left text-sm">
            <thead className="text-fg-muted">
              <tr>
                <Th>{t("admin.usage.col.day")}</Th>
                <Th numeric>{t("admin.usage.col.calls")}</Th>
                <Th numeric>{t("admin.usage.col.cost")}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {report.daily.map((d) => (
                <tr key={d.date}>
                  <Td>{label(d.date)}</Td>
                  <Td numeric>{f.number(d.calls)}</Td>
                  <Td numeric>{f.money(d.costs)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableRegion>
      </details>
    </Card>
  );
}

function Breakdown({ report }: { report: UsageReport }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <Card title={t("admin.usage.breakdownTitle")}>
      <h3 className="font-heading text-base font-semibold text-fg">{t("admin.usage.byTask")}</h3>
      <TableRegion label={t("admin.usage.byTask")}>
        <table className="mt-2 w-full text-left text-sm">
          <thead className="text-fg-muted">
            <tr>
              <Th>{t("admin.usage.col.task")}</Th>
              <Th numeric>{t("admin.usage.col.calls")}</Th>
              <Th numeric from="md">
                {t("admin.usage.col.failed")}
              </Th>
              <Th numeric from="md">
                {t("admin.usage.col.tokens")}
              </Th>
              <Th numeric from="sm">
                {t("admin.usage.col.latency")}
              </Th>
              <Th numeric>{t("admin.usage.col.cost")}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {report.byTask.map((row) => (
              <tr key={row.task}>
                <Td>{f.taskName(row.task)}</Td>
                <Td numeric>{f.number(row.calls)}</Td>
                <Td numeric from="md">
                  {f.number(row.failed)}
                </Td>
                <Td numeric from="md">
                  {f.number(row.tokens.input + row.tokens.output)}
                </Td>
                <Td numeric from="sm">{`${f.seconds(row.latencyMs.p50)} / ${f.seconds(row.latencyMs.p95)}`}</Td>
                <Td numeric>{f.money(row.costs)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableRegion>

      <h3 className="mt-6 font-heading text-base font-semibold text-fg">{t("admin.usage.byModel")}</h3>
      <TableRegion label={t("admin.usage.byModel")}>
        <table className="mt-2 w-full text-left text-sm">
          <thead className="text-fg-muted">
            <tr>
              <Th>{t("admin.usage.col.model")}</Th>
              <Th from="sm">{t("admin.usage.col.provider")}</Th>
              <Th numeric>{t("admin.usage.col.calls")}</Th>
              <Th numeric from="sm">
                {t("admin.usage.col.failed")}
              </Th>
              <Th numeric>{t("admin.usage.col.cost")}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {report.byModel.map((row) => (
              <tr key={`${row.provider}-${row.modelAlias}`}>
                <Td>{row.modelAlias}</Td>
                <Td from="sm">{row.provider}</Td>
                <Td numeric>{f.number(row.calls)}</Td>
                <Td numeric from="sm">
                  {f.number(row.failed)}
                </Td>
                <Td numeric>{f.money(row.costs)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableRegion>

      <h3 className="mt-6 font-heading text-base font-semibold text-fg">{t("admin.usage.errors")}</h3>
      {report.errors.length === 0 ? (
        <p className="mt-2 text-sm text-fg-muted">{t("admin.usage.noErrors")}</p>
      ) : (
        <TableRegion label={t("admin.usage.errors")}>
          <table className="mt-2 w-full text-left text-sm">
            <thead className="text-fg-muted">
              <tr>
                <Th>{t("admin.usage.col.task")}</Th>
                <Th>{t("admin.usage.col.code")}</Th>
                <Th numeric>{t("admin.usage.col.count")}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {report.errors.map((e) => (
                <tr key={`${e.task}-${e.code}`}>
                  <Td>{f.taskName(e.task)}</Td>
                  <Td>
                    <code className="break-all">{e.code}</code>
                  </Td>
                  <Td numeric>{f.number(e.count)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableRegion>
      )}
    </Card>
  );
}

const FUNNEL_STEPS = ["started", "submitted", "reachedReview", "decided"] as const;

function Funnel({ report }: { report: UsageReport }) {
  const { t } = useTranslation();
  const f = useFormat();
  const { overall } = report.funnel;
  const hours = (h: number | null) => (h === null ? "—" : t("admin.usage.hours", { value: f.number(h) }));
  const share = (count: number) => (overall.started > 0 ? count / overall.started : null);
  return (
    <Card title={t("admin.usage.funnelTitle")}>
      <p className="mb-4 max-w-prose text-sm text-fg-muted">{t("admin.usage.funnelHint")}</p>
      <ol className="flex flex-col gap-2">
        {FUNNEL_STEPS.map((step) => (
          <li key={step} className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] items-center gap-3 text-sm">
            <span className="text-fg">{t(`admin.usage.funnel.${step}`)}</span>
            <span className="flex items-center gap-2">
              <span aria-hidden="true" className="h-3 min-w-0 flex-1 rounded-full bg-bg">
                <span
                  className="block h-3 rounded-full bg-primary"
                  style={{ width: `${(share(overall[step]) ?? 0) * 100}%` }}
                />
              </span>
              <span className="w-24 shrink-0 text-right tabular text-fg">
                {f.number(overall[step])} · {f.percent(share(overall[step]))}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-sm text-fg-muted">
        {t("admin.usage.funnelOutcome", {
          approved: f.number(overall.approved),
          rejected: f.number(overall.rejected),
          abandoned: f.number(overall.abandoned),
          open: f.number(overall.open),
        })}
      </p>
      <p className="mt-1 text-sm text-fg-muted">
        {t("admin.usage.medians", {
          submit: hours(report.funnel.medianHoursToSubmit),
          decision: hours(report.funnel.medianHoursToDecision),
        })}
      </p>

      <h3 className="mt-6 font-heading text-base font-semibold text-fg">{t("admin.usage.byLegalEntity")}</h3>
      <TableRegion label={t("admin.usage.byLegalEntity")}>
        <table className="mt-2 w-full text-left text-sm">
          <thead className="text-fg-muted">
            <tr>
              <Th>{t("admin.usage.col.legalEntity")}</Th>
              {ENTITY_COLUMNS.map(([k, from]) => (
                <Th key={k} numeric from={from}>
                  {t(`admin.usage.funnel.${k}`)}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {report.funnel.byLegalEntity.map((row) => (
              <FunnelRow key={row.legalEntity?.code ?? "none"} row={row} />
            ))}
          </tbody>
        </table>
      </TableRegion>
    </Card>
  );
}

/** Per-legal-entity columns; phones show the first three counts (tables never scroll sideways). */
const ENTITY_COLUMNS = [
  ["started", undefined],
  ["submitted", undefined],
  ["reachedReview", "md"],
  ["approved", undefined],
  ["rejected", "md"],
  ["abandoned", "md"],
  ["open", "md"],
] as const satisfies readonly (readonly [keyof FunnelCounts, Breakpoint | undefined])[];

function FunnelRow({ row }: { row: FunnelCounts & { legalEntity: { code: string; name: string } | null } }) {
  const { t } = useTranslation();
  const f = useFormat();
  return (
    <tr>
      <Td>{row.legalEntity ? row.legalEntity.name : t("admin.usage.notSet")}</Td>
      {ENTITY_COLUMNS.map(([k, from]) => (
        <Td key={k} numeric from={from}>
          {f.number(row[k])}
        </Td>
      ))}
    </tr>
  );
}

/** A named table region. Tables fit the width: secondary columns appear from `sm` / `md` up. */
function TableRegion({ label, children }: { label: string; children: ReactNode }) {
  return <section aria-label={label}>{children}</section>;
}

type Breakpoint = "sm" | "md";
const SHOWN_FROM: Record<Breakpoint, string> = { sm: "hidden sm:table-cell", md: "hidden md:table-cell" };

function Th({ children, numeric = false, from }: { children: ReactNode; numeric?: boolean; from?: Breakpoint }) {
  return (
    <th
      scope="col"
      className={clsx("py-2 pr-4 font-semibold last:pr-0", numeric && "text-right", from && SHOWN_FROM[from])}
    >
      {children}
    </th>
  );
}

function Td({ children, numeric = false, from }: { children: ReactNode; numeric?: boolean; from?: Breakpoint }) {
  return (
    <td
      className={clsx(
        "py-2 pr-4 text-fg last:pr-0",
        numeric && "text-right tabular whitespace-nowrap",
        from && SHOWN_FROM[from],
      )}
    >
      {children}
    </td>
  );
}
